import { FullStackElementTree } from "@hub/models/content/full-stack-element-tree";
import { VersionStatus } from "@hub/models/elements/version-status";
import { AnchorLink } from "@hub/ui/anchor-link";
import { FullStackOptionButton } from "@hub/ui/widgets/full-stack/full-stack-option-button";
import { useFrameworkChooseHook } from "@hub/ui/widgets/full-stack/use-framework-choose.hook";
import { getPathVariant } from "@hub/utils/get-path-variant";
import React from "react";
import styles from "./full-stack-framework-chooser.module.css";

interface FullStackFrameworkChooserProps {
  optionsTitle: string;
  elementTree: FullStackElementTree;
  currentPath: string;
  nextLabel: string;
}

export const FullStackFrameworkChooser: React.FC<
  FullStackFrameworkChooserProps
> = ({ optionsTitle, elementTree, currentPath, nextLabel }) => {
  const {
    NOT_APPLICABLE,
    canCompleteFrameworkSelection,

    selectedFramework,
    selectedVersion,
    selectedLanguage,
    selectedVariant,

    versionOptions,
    DocumentPickerLanguageOptions,
    variantOptions,

    onFrameworkSelection,
    onVersionSelection,
    onLanguageSelection,
    onVariantSelection,
  } = useFrameworkChooseHook();

  return (
    <div className={styles.widget}>
      <div className={styles.section}>
        <h2 className={styles.section__title}>{optionsTitle}</h2>
        <div className={styles.section__options}>
          {Object.values(elementTree.frameworks).map((framework) => {
            const frameworkData = elementTree.frameworks[framework.data.key];

            if (!frameworkData) {
              return null;
            }

            return (
              <FullStackOptionButton
                key={framework.data.key}
                option={framework.data}
                isActive={selectedFramework === framework.data}
                onClick={() =>
                  onFrameworkSelection({
                    elementTree,
                    selections: { framework: framework.data },
                  })
                }
              />
            );
          })}
        </div>
      </div>
      {selectedFramework && versionOptions.length > 1 && (
        <div className={styles.section}>
          <h2 className={styles.section__title}>Choose a framework version</h2>
          <div className={styles.section__options}>
            {versionOptions.map((version) => {
              return (
                <FullStackOptionButton
                  key={version.key}
                  option={version}
                  isActive={selectedVersion === version}
                  compact={true}
                  onClick={() => {
                    onVersionSelection({
                      elementTree,
                      selections: { framework: selectedFramework, version },
                    });
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
      {selectedFramework && selectedVersion && (
        <div className={styles.section}>
          <h2 className={styles.section__title}>
            Choose a programming language
          </h2>
          <div className={styles.section__options}>
            {selectedFramework &&
              selectedVersion &&
              DocumentPickerLanguageOptions.map((language) => {
                const languageData =
                  elementTree.frameworks[selectedFramework.key]?.versions[
                    selectedVersion?.key
                  ]?.languages[language.key];

                if (!languageData) {
                  return null;
                }

                return (
                  <FullStackOptionButton
                    key={language.key}
                    option={language}
                    isActive={selectedLanguage === language}
                    onClick={() => {
                      onLanguageSelection({
                        elementTree,
                        selections: {
                          framework: selectedFramework,
                          version: selectedVersion,
                          language,
                        },
                      });
                    }}
                  />
                );
              })}
          </div>
        </div>
      )}
      {selectedFramework &&
        selectedVersion &&
        selectedLanguage &&
        selectedVariant?.key !== NOT_APPLICABLE && (
          <div className={styles.section}>
            <h2 className={styles.section__title}>Choose an option</h2>
            <div className={styles.section__options}>
              {variantOptions.map((variant) => {
                return (
                  <FullStackOptionButton
                    key={variant.key}
                    option={variant}
                    isActive={selectedVariant === variant}
                    onClick={() => {
                      onVariantSelection({ selections: { variant } });
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      <div className={styles.section__nextButtonContainer}>
        <AnchorLink
          className={styles.section__nextButton}
          to={
            canCompleteFrameworkSelection
              ? `${currentPath}/${selectedFramework?.key}${
                  selectedVersion &&
                  selectedVersion.status === VersionStatus.MAINTENANCE
                    ? `-v${selectedVersion.key}`
                    : ""
                }-${selectedLanguage?.key}${
                  selectedVariant &&
                  getPathVariant({
                    variant: selectedVariant.key,
                    variantInPath: selectedVariant.inPath,
                  })
                }`
              : ""
          }
          disabled={!canCompleteFrameworkSelection}
          options={{ button: true }}
        >
          {nextLabel}
        </AnchorLink>
      </div>
    </div>
  );
};
